import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { LogOut, LayoutDashboard, FileText, Wallet, Users, Banknote, MessageSquare, PlusCircle, ShieldCheck, FileSpreadsheet, MessageCircle, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import BrandLogo from "@/components/BrandLogo";

const clientLinks = [
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard },
  { to: "/dashboard/applications", label: "My Applications", icon: FileText },
  { to: "/dashboard/loans", label: "My Loans", icon: Wallet },
];

const adminLinks = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/applications", label: "Applications", icon: FileText },
  { to: "/admin/loans", label: "Loan Portfolio", icon: Banknote },
  { to: "/admin/arrears", label: "Arrears", icon: AlertTriangle },
  { to: "/admin/customers", label: "Customers (CRM)", icon: Users },
  { to: "/admin/messages", label: "Inbox", icon: MessageSquare },
  { to: "/admin/notifications", label: "Notifications", icon: MessageCircle },
  { to: "/admin/audit", label: "Audit log", icon: ShieldCheck },
];

export default function DashboardLayout({ children, role = "client" }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const links = role === "admin" ? adminLinks : clientLinks;

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  return (
    <div className="min-h-screen bg-dashboard">
      {/* Topbar */}
      <header className="sticky top-0 z-30 border-b border-[#E5E2DE] bg-white">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between px-4 py-3 sm:px-6">
          <Link to="/" className="flex items-center gap-3" data-testid="dash-logo-link">
            <BrandLogo size="sm" />
            <span className="hidden font-heading text-sm font-semibold text-ink sm:inline">
              {role === "admin" ? "Admin Console" : "Client Portal"}
            </span>
          </Link>
          <div className="flex items-center gap-3">
            {role === "client" && (
              <Link to="/apply">
                <Button className="bg-[#E87722] text-white hover:bg-[#cc6517]" data-testid="dash-apply-btn">
                  <PlusCircle className="mr-2 h-4 w-4" />
                  New Application
                </Button>
              </Link>
            )}
            <div className="hidden text-right sm:block">
              <div className="text-sm font-semibold text-ink">{user?.full_name}</div>
              <div className="text-xs text-ink-muted">{user?.email}</div>
            </div>
            <Button variant="ghost" onClick={handleLogout} data-testid="dash-logout-btn">
              <LogOut className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      <div className="mx-auto flex max-w-[1400px] gap-6 px-4 py-6 sm:px-6">
        {/* Sidebar */}
        <aside className="hidden w-60 shrink-0 lg:block">
          <nav className="space-y-1 rounded-xl border border-[#E5E2DE] bg-white p-3">
            {links.map((l) => {
              const Icon = l.icon;
              return (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.end}
                  data-testid={`sidebar-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                  className={({ isActive }) =>
                    `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                      isActive ? "bg-[#F9F6F0] text-[#5B2C82]" : "text-ink-soft hover:bg-[#F9F6F0] hover:text-[#5B2C82]"
                    }`
                  }
                >
                  <Icon className="h-4 w-4" />
                  {l.label}
                </NavLink>
              );
            })}
          </nav>
        </aside>

        {/* Main */}
        <main className="min-w-0 flex-1">
          {/* Mobile nav */}
          <div className="mb-4 flex gap-2 overflow-x-auto lg:hidden">
            {links.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.end}
                className={({ isActive }) =>
                  `whitespace-nowrap rounded-full border px-3 py-1.5 text-xs font-medium ${
                    isActive
                      ? "border-[#5B2C82] bg-[#5B2C82] text-white"
                      : "border-[#E5E2DE] bg-white text-ink-soft"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </div>
          {children}
        </main>
      </div>
    </div>
  );
}
